/**
 * Driver Status Board — port of the V1 Dispatch Dashboard "[R] Driver Status"
 * tab as a read-only standalone TanTable page (one row per dispatched leg for
 * the chosen day). Filters: Date, order Dispatcher(s), Hide Completed.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAdditionalFieldSelection, useAdditionalFieldColumns } from '../../hooks/useAdditionalFields';
import { TanTable } from '../../components/TanTable';
import FilterBar from '../../components/FilterBar';
import MultiSelect from '../../components/MultiSelect';
import DateTimePicker from '../../components/DateTimePicker';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import { useOrderDispatcherOptions } from '../../hooks/useDispatcherOptions';
import driverStatusBoardService from '../../services/driverStatusBoard.service';
import type { DriverStatusBoardRow, DriverStatusBoardSummary } from '../../types/driverStatusBoard';
import { buildDriverStatusBoardColumns, currentMoveIndicator, driverStatusRowClass } from './driverStatusBoardColumns';

const getRowId = (row: DriverStatusBoardRow) => String(row.legrecno);

const REFRESH_INTERVAL = 30000;

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function DriverStatusBoard() {
  const [boardDate, setBoardDate] = useState<Date>(() => new Date());
  const [dispatchers, setDispatchers] = useState<string[]>([]);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [rows, setRows] = useState<DriverStatusBoardRow[]>([]);
  const [summary, setSummary] = useState<DriverStatusBoardSummary>({ drivers: 0, pros: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  const { options: dispatcherOptions, loading: dispatchersLoading } = useOrderDispatcherOptions();

  const fieldSelection = useAdditionalFieldSelection('driver-status-board', { binding: 'all' });
  const { additionalFields, ready: fieldsReady } = fieldSelection;

  const additionalFieldsKey = additionalFields.join(',');

  const reqIdRef = useRef(0);
  const load = useCallback(async (silent = false) => {
    if (!fieldsReady) return;
    const myId = ++reqIdRef.current;
    if (!silent) setLoading(true);
    try {
      const data = await driverStatusBoardService.getDriverStatusBoard({
        date: toYmd(boardDate),
        dispatchers,
        hide_completed: hideCompleted,
        additional_fields: additionalFieldsKey ? additionalFieldsKey.split(',') : [],
      });
      if (myId !== reqIdRef.current) return;
      setRows(data.rows);
      setSummary(data.summary);
      setError(null);
    } catch (e: any) {
      if (myId !== reqIdRef.current) return;
      setError(e?.message ?? 'Failed to load driver status board');
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  }, [boardDate, dispatchers, hideCompleted, fieldsReady, additionalFieldsKey]);

  useEffect(() => { load(); }, [load]);
  useVisibilityAwareInterval(() => { load(true); }, REFRESH_INTERVAL);

  const baseColumns = useMemo(() => buildDriverStatusBoardColumns(setSelectedPro), []);

  const { columns, availableFields } = useAdditionalFieldColumns<DriverStatusBoardRow>(
    fieldSelection, { baseColumns, binding: 'all', coveredFields: { outgate: ['outgatedatetime'] } },
  );

  return (
    <div className="flex flex-col h-full">
      <FilterBar>
        <FilterBar.Group label="Date">
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={boardDate}
            onChange={(val) => { if (val instanceof Date) setBoardDate(val); }}
            inputVariant="compact"
            labelType="none"
            autoApply
          />
        </FilterBar.Group>
        <FilterBar.Divider />
        <MultiSelect
          options={dispatcherOptions}
          value={dispatchers}
          onChange={setDispatchers}
          loading={dispatchersLoading}
          placeholder="All dispatchers"
          enableSearch
          showClearOption
          clearOptionLabel="All dispatchers"
          size="sm"
          usePortal
          preserveOrder
          className="min-w-[14rem]"
        />
        <FilterBar.Divider />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={hideCompleted}
            onChange={(e) => setHideCompleted(e.target.checked)}
          />
          Hide Completed
        </label>
        <span className="ml-auto text-sm text-muted whitespace-nowrap">
          {summary.drivers} drivers · {summary.pros} pros
        </span>
      </FilterBar>

      {error && (
        <div className="px-6 py-2 text-sm text-red-700 bg-red-50 border-b border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800">
          {error}
        </div>
      )}

      <div className="flex-1 min-h-0 p-4">
        <TanTable
          data={rows}
          columns={columns}
          availableFields={availableFields}
          additionalFields={additionalFields}
          onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
          loading={loading}
          settingsKey="driver-status-board"
          cardTitle="Driver Status Board"
          searchPlaceholder="Search drivers, pros, locations..."
          enableColumnFilters
          enableSearch
          enableExcelExport
          enableGrouping
          getRowId={getRowId}
          getIndicatorInfo={currentMoveIndicator}
          defaultPageSize={200}
          getRowClassName={driverStatusRowClass}
          fillContainer
        />
      </div>

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={selectedPro !== null}
        onClose={() => setSelectedPro(null)}
      />
    </div>
  );
}
